/**
 * The host-page CTA contract.
 *
 * floe-site's marketing CTAs ("See Floe Live" in the nav, hero, footer and every
 * page scaffold) surface the agent ALREADY mounted on the page rather than
 * booting a second overlay. They do it with a window event, because the host
 * page and the SDK are separately-deployed artifacts — floe-site ships from its
 * own build, the SDK from cdn.floe.so — with no import edge between them, so a
 * shared constant is not available to both sides.
 *
 * That contract broke silently once, and the shape of the failure is why this
 * module exists. The event's only listener lived in DemoView; floe.so switched
 * to the website agent (`websiteAgent: true`, so DemoView never mounts); every
 * "See Floe Live" button on the site became a no-op. Nothing failed loudly — a
 * CustomEvent with no listener is indistinguishable from one that was handled,
 * and the host's Calendly fallback only fires when the SDK isn't configured at
 * all, which it was. A WORKING config is what hid it.
 *
 * So the whole contract lives here:
 *   - the event name, imported by BOTH listeners (the demo intro pill and the
 *     website agent) instead of being retyped as a literal at each end,
 *   - the truth table for what the CTA should do given the agent's live state.
 *
 * And the event is dispatched `cancelable`: a listener that acts on it calls
 * `preventDefault()`, which makes `dispatchEvent()` return false, which is how
 * the host page learns the CTA actually landed. When nothing claims it the host
 * falls back to booking a call — so the next time a mode switch orphans this
 * listener, the visitor still gets somewhere and we still hear about it.
 */

/**
 * The window event a host-page demo CTA dispatches.
 *
 * Kept at its historical name deliberately. floe-site and the CDN bundle deploy
 * independently, so a rename would open a window where the newly-shipped half
 * listens for (or fires) a name the other half has never heard of — which is a
 * fresh instance of the exact bug this module documents. The name is a wire
 * format now; only its meaning has widened.
 */
export const HOST_DEMO_CTA_EVENT = 'floe:expand-demo-pill';

/** The agent state a host CTA has to reason about. */
export interface HostDemoCtaState {
  /** Does this site have a demo to show? (`demoAvailable` from validate-client-key.) */
  demoAvailable: boolean;
  /**
   * Is the website agent entitled to start a session at all?
   * (`websiteAgentEnabled` from validate-client-key.)
   *
   * Separate from `demoAvailable`, which answers a question about the DEMO
   * agent — `demoAgentEnabled && isDemoReady`. A site can be entitled to the
   * demo agent and not the website agent, so `demoAvailable` alone will happily
   * green-light a CTA whose session floe-api then rejects: `/start-session`
   * with `entryPhase: 'q_and_a'` throws Forbidden unless `websiteAgentEnabled`.
   * The visitor would get a claimed CTA, a 403, and no fallback — the precise
   * silent no-op this contract exists to make impossible.
   *
   * Defaults to `true` when absent rather than false: this field and
   * `demoAvailable` ship from the same floe-api version, and `demoAvailable`
   * already fails closed, so treating "not reported" as "not entitled" would
   * only ever disable a CTA that works.
   */
  websiteAgentEnabled: boolean;
  /** Is a demo already running (screencast attached)? */
  demoActive: boolean;
  /** Has THIS page load booted a pipecat session? */
  sessionStarted: boolean;
  /** Is the panel in the post-End "ended" state? */
  ended: boolean;
}

export interface HostDemoCtaAction {
  /**
   * Claim the event (`preventDefault()`), telling the host page the CTA landed.
   * False leaves the event unclaimed so the host can fall back to Calendly.
   */
  claim: boolean;
  /**
   * Open the panel. The view keeps it shut when a presenter is on the page:
   * the call then lives in the avatar dock, for every branch below.
   */
  open: boolean;
  /** Boot a session before sending the hidden demo control. */
  start: boolean;
  /** Session is already live — forward the hidden demo control. */
  forward: boolean;
}

/**
 * What a host-page demo CTA does, given the agent's current state.
 *
 * The cases, in order:
 *
 *   no demo available — do NOT claim it. The site has no demo to show, so the
 *     honest outcome is the host's own fallback (book a call), not a panel that
 *     opens and then can't deliver. This is the one branch that leaves the
 *     event uncancelled on purpose.
 *   demo already running — open only. Re-asking for a demo the visitor is
 *     already watching would be a nonsense turn; the CTA here means "show me
 *     the thing I already started", which is just surfacing the panel.
 *   ended, or no session yet — start. The hidden control is queued behind that
 *     boot and clears the ended state without creating a visitor transcript row.
 *   live session — forward the hidden control. The visitor may have collapsed
 *     the panel mid-chat; `open` reopens it first.
 *
 * `start` and `forward` are mutually exclusive, so one click creates one request.
 */
/**
 * How long {@link surfaceDemo} keeps asking before reporting the request
 * unclaimed. Long enough to cover a cold boot (React mount +
 * validate-client-key), short enough that a caller who falls back with
 * `window.open` is still inside the browser's transient-activation window (~5s)
 * and not silently popup-blocked.
 */
export const DEMO_REQUEST_TIMEOUT_MS = 1500;

/**
 * Ask the mounted agent to surface the demo, and resolve with whether anything
 * claimed the request.
 *
 * This is the whole host-page CTA dance in one place. It exists because
 * "unclaimed" has two causes a single attempt cannot tell apart — nobody is
 * listening, or the agent has not subscribed *yet* — so every caller that fires
 * once and believes the answer is wrong roughly as often as the page is cold.
 *
 * The deadline is owned by a timer, NOT the frame loop: browsers pause
 * requestAnimationFrame outright in a hidden tab, and a rAF-only loop strands
 * the answer until the tab is revealed. The timer takes one final shot before
 * giving up, because in a hidden tab the loop may never have run at all — so
 * that call is the FIRST real attempt rather than the last.
 */
export function surfaceDemo(options: { timeoutMs?: number } = {}): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);

  const ask = () =>
    !window.dispatchEvent(new CustomEvent(HOST_DEMO_CTA_EVENT, { cancelable: true }));

  const budget = options.timeoutMs ?? DEMO_REQUEST_TIMEOUT_MS;
  // An explicit zero/negative budget means "ask once, tell me now" — used by
  // callers that must stay synchronous with a user gesture.
  if (!(budget > 0)) return Promise.resolve(ask());

  return new Promise<boolean>((resolve) => {
    // The timer and the frame loop race; whichever finishes first wins and the
    // other must go inert, or the promise would settle twice.
    let settled = false;
    let frame = 0;
    let timer = 0;

    const finish = (claimed: boolean) => {
      if (settled) return;
      settled = true;
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      resolve(claimed);
    };

    const attempt = () => {
      if (settled) return;
      if (ask()) {
        finish(true);
        return;
      }
      frame = requestAnimationFrame(attempt);
    };

    timer = window.setTimeout(() => finish(ask()), budget);
    frame = requestAnimationFrame(attempt);
  });
}

export function hostDemoCtaAction(state: HostDemoCtaState): HostDemoCtaAction {
  // Both gates leave the event UNCLAIMED, and for the same reason: the honest
  // outcome is the host's own fallback, not a panel that opens and then cannot
  // deliver. `websiteAgentEnabled` is the entitlement to run the session at all;
  // `demoAvailable` is whether there is a demo at the end of it.
  if (!state.demoAvailable || !state.websiteAgentEnabled) {
    return { claim: false, open: false, start: false, forward: false };
  }
  if (state.demoActive) {
    return { claim: true, open: true, start: false, forward: false };
  }
  if (state.ended || !state.sessionStarted) {
    return { claim: true, open: true, start: true, forward: false };
  }
  return { claim: true, open: true, start: false, forward: true };
}
